import { useInView } from 'react-cool-inview';

import { useTracker } from '@r123/segment-tracker';

import { Property } from '@/modules/core/gql/generated';
import generatePayloadNPL from '@/modules/segment/helper/generatePayloadNPL';
import generatePayloadProperty from '@/modules/segment/helper/generatePayloadProperty';
import generatePayloadSpecialOffer from '@/modules/segment/helper/generatePayloadSpecialOffer';
import generatePromotion from '@/modules/segment/helper/generatePromotion';
import { SegmentTrackerEventEnum } from '@/modules/segment/segment.interface';

import { getSRPVariant } from '../../_helper/getSRPVariant';

const useSRPCardImpressAndClickSegment = ({
    property,
    index,
    placement_name,
    current_page_type,
    feature_section_name,
    threshold = 0.8,
    page_type,
    getPayloadOnEnter,
}: {
    property: Property;
    index: number;
    placement_name: string;
    feature_section_name?: string;
    current_page_type?: string;
    threshold?: number;
    page_type?: string;

    getPayloadOnEnter?: () => { [key: string]: string | undefined };
}) => {
    const { sendTracker } = useTracker();
    const trackerProperty = generatePayloadProperty(property);
    const trackerSpecialOffer = generatePayloadSpecialOffer({
        priceDrop: property.priceDrop,
        specialFlag: property.specialFlag,
    });

    const trackerNPL = generatePayloadNPL({
        placement_name,
        agent: property.agent,
        additionalFlags: property.additionalFlags,
        auction: property.auction,
        isNpl: property.specialFlag?.isNpl,
    });

    const promotions = generatePromotion(property, true);

    const defaultTracker = {
        ...trackerProperty,
        ...trackerSpecialOffer,
        ...trackerNPL,
        ...promotions,
        listing_position_in_placement: index,
        placement_name,
        current_page_type,
        feature_section_name,
        page_type,
        feature_srp_variant: ['SRP', 'NSRP'].includes(page_type || '')
            ? getSRPVariant()
            : undefined,
    };

    const { observe } = useInView({
        threshold,
        unobserveOnEnter: true,
        onEnter: () => {
            const params = getPayloadOnEnter ? getPayloadOnEnter() : {};
            sendTracker(SegmentTrackerEventEnum.PropertyImpressed, {
                ...defaultTracker,
                ...params,
            });
        },
    });

    const onClickCard = (params?: { [key: string]: any }) => {
        sendTracker(SegmentTrackerEventEnum.PropertyClicked, {
            ...defaultTracker,
            feature_one_page_after_url: `${typeof window !== 'undefined' ? window.location.origin : ''}${property.slug || property.url}`,
            ...(params || {}),
        });
    };

    const onImpressedUnit = (unit: Property, isFromCluster?: boolean) => {
        const trackerUnit = generatePayloadProperty(unit);
        const promotionUnit = generatePromotion(property);
        const placementName = !isFromCluster ? 'SRP' : 'SRP-Modal Cluster';
        const defaultTrackerUnit = {
            ...trackerUnit,
            ...promotionUnit,
            listing_position_in_placement: index,
            placement_name: placementName,
            current_page_type,
            feature_section_name,
            page_type,
            feature_srp_variant: ['SRP', 'NSRP'].includes(page_type || '')
                ? getSRPVariant()
                : undefined,
        };
        sendTracker(SegmentTrackerEventEnum.PropertyImpressed, {
            ...defaultTrackerUnit,
            feature_one_page_after_url: `${typeof window !== 'undefined' ? window.location.origin : ''}${property.slug || property.url}`,
        });
    };
    return { ref: { observe }, action: { onClickCard, onImpressedUnit } };
};

export default useSRPCardImpressAndClickSegment;
